import { useUpdateEffect } from 'ahooks';
import React from 'react';
import ReactFocusLock from 'react-focus-lock';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import { useAppSelector } from '../../../../../hooks/hooks';
import useBodyScrollLock from '../../../../../hooks/useBodyScrollLock';
import useContext from '../../../../../hooks/useContext';
import store from '../../../../../store';
import { dataGridActions } from '../../../../../store/dataGridSlice/dataGridSlice';
import useIsMaximized from '../../../common/lists/hooks/useIsMaximized';
import fieldsStore from '../../fieldsStore/fieldsStore';
import useApiaFieldCommonProps from '../useApiaFieldCommonProps';
import { DataGridIdentifier } from './context';
import DataGridFooter from './Footer';
import KeyHandler from './KeyHandler';
import Lock from './Lock';
import QueryGridRenderer from './QueryGridRenderer';
import Table from './Table';

const ApiaDataGridRenderer = () => {
  const { gridId } = useContext(DataGridIdentifier);

  const [tableWrapper, setTableWrapper] = React.useState<HTMLDivElement | null>(
    null,
  );
  const { cssClass, gridTitle, height, isQuery, ariaLabel, ariaDescription } =
    useAppSelector((global) => {
      const state = global.dataGridSlice[gridId];
      const { gridHeight } = state.context.grid.field.properties;
      return {
        cssClass: state.context.grid.field.properties.cssClass,
        height: gridHeight ? `${gridHeight}px` : '360px',
        isQuery: !!state.context.grid.field.properties.gridQuery,
        gridTitle:
          state.context.grid.field.properties?.title ||
          state.context.grid.field.properties.gridTitle,
        ariaLabel: state.context.grid.field.properties?.ariaLabel,
        ariaDescription: state.context.grid.field.properties?.ariaDescription,
      };
    }, shallowEqual);
  const lockRef = React.useRef<HTMLDivElement>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  useUpdateEffect(
    function handleJSApiEvents() {
      function handleAdd() {
        fieldsStore.getById(gridId).fireEvent('gridAdd');
      }

      tableWrapper?.addEventListener('gridAdd', handleAdd);

      return () => {
        tableWrapper?.removeEventListener('gridAdd', handleAdd);
      };
    },

    [tableWrapper, gridId],
  );

  const { identifier } = useApiaFieldCommonProps();

  React.useLayoutEffect(() => {
    fieldsStore.getById(gridId).markAsLoaded();
  }, [gridId]);

  const handleFocus = React.useCallback(() => {
    store.dispatch(dataGridActions.update({ id: gridId, isGridFocused: true }));
  }, [gridId]);
  const handleBlur = React.useCallback(() => {
    store.dispatch(
      dataGridActions.update({ id: gridId, isGridFocused: false }),
    );
  }, [gridId]);

  const sx = React.useMemo(() => ({ height }), [height]);

  const maximizationId = identifier.id + identifier['data-name'];
  const { isMaximized: isFullScreen, toggleIsMaximized: setIsFullScreen } =
    useIsMaximized(maximizationId);
  useBodyScrollLock(isFullScreen);

  React.useEffect(() => {
    function exitFullScreen(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setIsFullScreen(false);
    }
    document.addEventListener('keydown', exitFullScreen);
    return () => {
      document.removeEventListener('keydown', exitFullScreen);
    };
  }, [setIsFullScreen]);

  if (isQuery) return <QueryGridRenderer />;

  return (
    <ReactFocusLock
      disabled={!isFullScreen}
      autoFocus={false}
      returnFocus={false}
      className={`grid__renderer ${cssClass ?? ''}`}
      crossFrame={false}
    >
      <Box
        ref={setTableWrapper}
        {...identifier}
        sx={sx}
        {...window.getVariant('layout.execution.form.fields.dataGrid')}
        id={gridId}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <Box
          ref={wrapperRef}
          className={`grid__container  ${isFullScreen ? 'fullscreen' : ''}`}
        >
          <KeyHandler className="grid__wrap">
            <Table
              title={gridTitle}
              ariaLabel={ariaLabel}
              ariaDescription={ariaDescription}
            />
          </KeyHandler>
          <DataGridFooter maximizationId={maximizationId} />
        </Box>
        <Lock ref={lockRef} />
      </Box>
    </ReactFocusLock>
  );
};

export default React.memo(ApiaDataGridRenderer);
